import { AssetType } from '@arcxgame/contracts/dist/arc-types/core'
import { useErc20Details } from '@arcxmoney/arcx-react'
import { useWeb3React } from '@web3-react/core'
import { BigNumber, BigNumberish, constants } from 'ethers'
import React, { useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { ArcContext } from 'src/contexts/ArcContext'
import { useErc20Balance } from 'src/hooks/useErc20Balance'
import { usePositions } from 'src/hooks/usePositions'
import { Synth } from 'src/services/GraphService'
import { calculateCollateralRatio } from '../../helpers/calculateCollateralRatio'
import { calculateLiquidationPrice } from '../../helpers/calculateLiquidationPrice'
import { Spinner } from '../Spinner'
import { getDescription } from './getDescription'
import { getPathForPosition } from './getPathForPosiition'
import { Manage } from './Manage'
import './Manage.scss'
import './ManageNew.scss'

interface IProps {
  synths: Synth[]
}

export function ManageNew({ synths }: IProps) {
  const history = useHistory()
  const { account } = useWeb3React()
  const { arc, getPrice, setIsLoading, getSynth } = useContext(ArcContext)
  const existingPositions = usePositions(account)

  const [synth, setSynth] = useState(synths[0])
  useEffect(() => {
    setSynth(synths[0])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [synths.toString()])
  const collateralDetails = useErc20Details(synth.collateralAddress, arc?.signer)
  const syntheticDetails = useErc20Details(synth.syntheticAddress, arc?.signer)
  const collateralBalance = useErc20Balance(synth.collateralAddress, arc?.signer)

  const price = synth && getPrice(synth.oracle)

  const [enteredDebtAmount, setEnteredDebtAmount] = useState<BigNumber>(constants.Zero)

  const [enteredCollateralAmount, setEnteredCollateralAmount] =
    useState<BigNumber>(collateralBalance)

  function getExitingVaultIdForCollateral() {
    if (existingPositions?.positions) {
      for (const position of existingPositions.positions) {
        const positionSynth = getSynth(position.synth)

        if (synth.collateralAddress === positionSynth.collateralAddress) {
          return position.id
        }
      }
    }
  }

  const existingVaultId = getExitingVaultIdForCollateral()
  const collateralAmountChanged = {
    change: enteredCollateralAmount,
    isPositive: true,
  }
  const debtAmountChanged = { change: enteredDebtAmount, isPositive: true }

  useEffect(() => {
    setEnteredCollateralAmount(collateralBalance)
  }, [synth, collateralBalance])

  if (!collateralDetails || !syntheticDetails) {
    return <Spinner />
  }

  const mintAndRedirect = async (existingVaultId: string) => {
    setIsLoading(true)
    try {
      let positionId: BigNumberish
      if (existingVaultId) {
        positionId = BigNumber.from(existingVaultId.split('-')[1])
        // Borrow on the existing vault
        await arc.approveStableShare(collateralAmountChanged.change, synth.name)
        await arc.borrow(
          positionId,
          collateralAmountChanged.change,
          debtAmountChanged.change,
          synth.name,
          arc?.signer,
        )
      } else {
        const position = await arc.openPosition(
          collateralAmountChanged.change,
          debtAmountChanged.change,
          synth.name,
          undefined,
          {
            gasLimit: '900000',
          },
        )

        positionId = position.params.id
      }

      history.push(getPathForPosition(synth.id, positionId), {
        isSucceed: true,
      })
    } catch (err) {
      console.error('onMintClicked', err)
    } finally {
      setIsLoading(false)
    }
  }

  const onMintClicked = async () => {
    mintAndRedirect(existingVaultId)
  }

  const currentCollateralRatio = calculateCollateralRatio(
    AssetType.Collateral,
    normalizeValue(enteredCollateralAmount),
    enteredDebtAmount,
    price,
  )

  function normalizeValue(value: BigNumber) {
    return value.mul(constants.WeiPerEther).div(BigNumber.from(10).pow(collateralDetails.decimals))
  }

  const existingVaultNotice = (
    <div className="manage-new__existing-vault-container">
      It looks like you already have an existing vault with the selected collateral type. Minting
      will deposit your collateral into{' '}
      <a href={`/vaults/${existingVaultId}`} target="_blank" rel="noopener noreferrer">
        that respective vault
      </a>
      .
    </div>
  )

  return (
    <>
      <Manage
        title="Mint"
        description={
          <>
            Mint StableX tokens to get started. Please see the{' '}
            <a target="_blank" rel="noreferrer" href="https://wiki.arcx.money/minting-stablex">
              wiki
            </a>{' '}
            for more details
          </>
        }
        collateralFieldTitle="Deposit"
        existingVaultNotice={existingVaultId && existingVaultNotice}
        syntheticFieldTitle="Mint"
        actionTitle="Mint"
        availableSynths={synths}
        synth={synth}
        setSynth={setSynth}
        onPrimaryClick={onMintClicked}
        changeDescription={getDescription(
          debtAmountChanged,
          {
            ...collateralAmountChanged,
            change: normalizeValue(enteredCollateralAmount),
          },
          synth,
        )}
        setEnteredCollateralAmount={setEnteredCollateralAmount}
        setEnteredSyntheticAmount={setEnteredDebtAmount}
        currentCollateralRatio={currentCollateralRatio}
        currentLiquidationPrice={calculateLiquidationPrice(
          normalizeValue(enteredCollateralAmount),
          enteredDebtAmount,
          price,
          synth,
        )}
        maxSyntheticAmount={normalizeValue(
          enteredCollateralAmount.mul(price).div(synth.collateralRatio),
        )}
        isEnoughTokens={collateralBalance.gte(enteredCollateralAmount)}
        collateralDetails={collateralDetails}
        collateralBalance={collateralBalance}
      />
    </>
  )
}
